import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import Authuser, { authCanPerform, evaluatePermissions } from '@/types/base/authuser'
import { PermAction, PermOptions, permOptionsKey, TableWithPermissions } from '@/types/base/permissions'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.mcps')

export const getMcpById = async (supabase: SupabaseClient<Database>, mcpId: string) => {
  const { data: mcp, error } = await supabase
    .from("mcps")
    .select("*")
    .eq("id", mcpId)
    .single()

  if (!mcp) {
    logError(logger, error, 'Failed to fetch MCP by ID', { mcpId: mcpId })
    throw new Error(error.message)
  }

  return mcp
}

export const getMcpsByIds = async (
  supabase: SupabaseClient<Database>,
  mcpIds: string[]
) => {
  const { data: mcps, error } = await supabase
    .from("mcps")
    .select("*")
    .in("id", mcpIds)

  if (error) {
    logError(logger, error, 'Failed to fetch MCP by IDs', { mcpIds: mcpIds })
    throw new Error(error.message)
  }

  return mcps
}

export const getOptionalMcpById = async (supabase: SupabaseClient<Database>, mcpId: string) => {
  const { data: mcp, error } = await supabase
    .from("mcps")
    .select("*")
    .eq("id", mcpId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional MCP by ID', { mcpId: mcpId })
    throw new Error(error.message)
  }

  return mcp
}

export const getOptionalSystemMcpByName = async (supabase: SupabaseClient<Database>, mcpName: string) => {
  const { data: mcp, error } = await supabase
    .from("mcps")
    .select("*")
    .eq("name", mcpName)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional MCP by ID', { mcpName: mcpName })
    throw new Error(error.message)
  }

  return mcp
}

export const getOptionalMcpByName = async (supabase: SupabaseClient<Database>, mcpName: string,
		workspaceId: string) => {
  const { data, error } = await supabase
    .from("workspaces")
    .select(
      `
      mcps (*)
    `
    )
    .eq("id", workspaceId)
    .eq("mcps.name", mcpName)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional MCP by name', { mcpName: mcpName, workspaceId })
    throw new Error(error.message)
  }

  return data ? data.mcps[0] : null
}

export const getMcpWorkspacesByWorkspaceId = async (supabase: SupabaseClient<Database>, workspaceId: string) => {
  const { data: workspace, error } = await supabase
    .from("workspaces")
    .select(
      `
      id,
      name,
      mcps (*)
    `
    )
    .eq("id", workspaceId)
    .single()

  if (!workspace) {
    logError(logger, error, 'Failed to fetch MCP workspaces by workspace ID', { workspaceId })
    throw new Error(error.message)
  }

  return workspace.mcps ?? []
}

export const getMcpsWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'mcps'>[]> => {
	const wsMcps = await getMcpWorkspacesByWorkspaceId(supabase, workspaceId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'mcps'>[] = []

	for (const [i, mcp] of wsMcps.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: "mcps", context: "ANY", uuid: mcp.id, tenant:mcp.owning_tenant, ws: workspace})

		if (i === 0) { // Attach special non specific permission to first record only
			const options = { type: "mcps", action: PermAction.create, context: "aidonAdmin" } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const shareKey = permOptionsKey(options)
			perms[shareKey] = allowed
		}

		result.push({ ...mcp, permissions: perms })
	}

	return result
}

export const getMcpWorkspacesByMcpId = async (supabase: SupabaseClient<Database>, mcpId: string) => {
  const { data: mcp, error } = await supabase
    .from("mcps")
    .select(
      `
      id,
      name,
      workspaces (*)
    `
    )
    .eq("id", mcpId)
    .single()

  if (!mcp) {
    logError(logger, error, 'Failed to fetch MCP workspaces by MCP ID', { mcpId: mcpId })
    throw new Error(error.message)
  }

  return mcp
}

export const getAllMcps = async (supabase: SupabaseClient<Database>) => {
  const { data: mcps, error } = await supabase
    .from("mcps")
    .select("*")

  if (!mcps) {
    logError(logger, error, 'Failed to fetch all MCPs')
    throw new Error(error.message)
  }

  return mcps
}

export const getAutoShareMcps = async (supabase: SupabaseClient<Database>) => {
  const { data: mcps, error } = await supabase
    .from("mcps")
    .select("*")
    .eq("auto_share", true)

  if (error) {
    logError(logger, error, 'Failed to fetch auto-share MCPs')
    throw new Error(error.message)
  }

  return mcps
}

export const createMcp = async (
	supabase: SupabaseClient<Database>,
  mcp: TablesInsert<"mcps">,
  workspace: Tables<"workspaces">
) => {
	const mcpWithTenant = {
		...mcp,
		owning_tenant: workspace.tenant_id
	}

  const { data: createdMcp, error } = await supabase
    .from("mcps")
    .insert([mcpWithTenant])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create MCP', { mcp: printObject(mcp) })
    throw new Error(error.message)
  }

  await createMcpWorkspace(supabase, {
    user_id: createdMcp.user_id,
    mcp_id: createdMcp.id,
    workspace_id: workspace.id
  })

  return createdMcp
}

export const createMcps = async (
	supabase: SupabaseClient<Database>,
  mcps: TablesInsert<"mcps">[],
  workspace: Tables<"workspaces">
) => {
	const mcpsWithTenant = mcps.map(mcp => ({
		...mcp,
		owning_tenant: workspace.tenant_id
	}))

  const { data: createdMcps, error } = await supabase
    .from("mcps")
    .insert(mcpsWithTenant)
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create MCPs', { mcps: printObject(mcps) })
    throw new Error(error.message)
  }

  await createMcpWorkspaces(supabase,
    createdMcps.map(mcp => ({
      user_id: mcp.user_id,
      mcp_id: mcp.id,
	    workspace_id: workspace.id
    }))
  )

  logger.info('MCPs created successfully', { mcpIds: createdMcps.map(mcp => mcp.id) })
  return createdMcps
}

export const createMcpWorkspace = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  mcp_id: string
  workspace_id: string
}) => {
  const { data: createdMcpWorkspace, error } = await supabase
    .from("mcp_workspaces")
    .insert([item])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create MCP workspace', { item: printObject(item) })
    throw new Error(error.message)
  }

  logger.info('MCP workspace created successfully', { createdMcpWorkspace: createdMcpWorkspace })
  return createdMcpWorkspace
}

export const createMcpWorkspaces = async (
	supabase: SupabaseClient<Database>,
  items: { user_id: string; mcp_id: string; workspace_id: string }[]
) => {
  const { data: createdMcpWorkspaces, error } = await supabase
    .from("mcp_workspaces")
    .upsert(items, { onConflict: 'mcp_id, workspace_id' })
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create MCP workspaces', { items: printObject(items) })
    throw new Error(error.message)
	}
  logger.info('MCP workspaces created successfully', { createdMcpWorkspaces: createdMcpWorkspaces })
  return createdMcpWorkspaces
}

export const updateMcp = async (
	supabase: SupabaseClient<Database>,
  mcpId: string,
  mcp: TablesUpdate<"mcps">
) => {
  const { data: updatedMcp, error } = await supabase
    .from("mcps")
    .update(mcp)
    .eq("id", mcpId)
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to update MCP', { mcpId, mcp: printObject(mcp) })
    throw new Error(error.message)
  }

  return updatedMcp
}

export const deleteMcp = async (supabase: SupabaseClient<Database>, mcpId: string) => {
  const { error } = await supabase.from("mcps").delete().eq("id", mcpId)

  if (error) {
    logError(logger, error, 'Failed to delete MCP', { mcpId: mcpId })
    throw new Error(error.message)
  }

  return true
}

export const deleteMcpWorkspace = async (
	supabase: SupabaseClient<Database>,
  mcpId: string,
  workspaceId: string
) => {
  const { error } = await supabase
    .from("mcp_workspaces")
    .delete()
    .eq("mcp_id", mcpId)
    .eq("workspace_id", workspaceId)

  if (error) {
    logError(logger, error, 'Failed to delete MCP workspace', { mcpId: mcpId, workspaceId })
    throw new Error(error.message)
	}
  return true
}
